import type { ShopperAiIntegrationId } from "@redotech/effect-api-shared/ids/definitions";
import type { ConversationThread } from "@redotech/effect-api-shared/models/shopper-ai/conversation/thread";
import type { IntegrationWithChannelDataResponse } from "@redotech/effect-api-shared/models/shopper-ai/integration/integration";
import { useHandler } from "@redotech/react-util/hook";
import MailIcon from "@redotech/redo-icons/mail-01.svg";
import MessageSquareIcon from "@redotech/redo-icons/message-square-01.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { Tooltip } from "@redotech/redo-web/tooltip/tooltip";
import { Fragment, memo, useMemo, useState } from "react";
import { useThreadMessages } from "../../hooks/use-conversation-data";
import { useCreateDraftThread } from "../../hooks/use-create-conversation";
import { useIntegrations } from "../../hooks/use-integrations";
import { ContactFormMessageCard } from "../message-types/contact-form-message-card";
import { ThreadHeader } from "../thread-header";

/**
 * Thread view for contact form submissions.
 * Renders the contact form message(s) as labeled-field cards mirroring the
 * customer-facing contact form. Exposes "Reply with email" / "Reply with SMS"
 * buttons that create a new draft thread on the conversation using the
 * integration defaults configured on the contact form.
 */
export const ContactFormThreadView = memo(function ContactFormThreadView({
  thread,
}: {
  thread: ConversationThread;
}) {
  const { data: messages } = useThreadMessages(
    thread.conversationId,
    thread.id,
  );

  const contactFormMessages = useMemo(
    () => messages.filter((m) => m.channel === "contactForm"),
    [messages],
  );

  const [isCollapsed, setIsCollapsed] = useState(false);
  const toggleCollapsed = useHandler(() => setIsCollapsed((prev) => !prev));

  const { data: integrations } = useIntegrations();
  const contactFormIntegration = useMemo(
    () =>
      integrations?.find(
        (
          i,
        ): i is Extract<
          IntegrationWithChannelDataResponse,
          { integrationType: "contactForm" }
        > =>
          i.integrationType === "contactForm" && i.id === thread.integrationId,
      ),
    [integrations, thread.integrationId],
  );
  const defaultEmailIntegrationId =
    contactFormIntegration?.channelData?.emailIntegrationId ?? null;
  const defaultSmsIntegrationId =
    contactFormIntegration?.channelData?.smsIntegrationId ?? null;

  const latest = contactFormMessages[contactFormMessages.length - 1];
  const customerEmail =
    latest?.channel === "contactForm" ? latest.channelData?.email : null;
  const customerPhone =
    latest?.channel === "contactForm" ? latest.channelData?.phone : null;
  const submissionSubject =
    latest?.channel === "contactForm" ? latest.channelData?.subject : null;
  const replySubject = submissionSubject
    ? `Re: ${submissionSubject}`
    : "Re: Your contact form submission";

  const { createDraftThread, isCreatingDraftThread } = useCreateDraftThread();

  const openReply = useHandler(
    async (integrationId: ShopperAiIntegrationId, kind: "email" | "sms") => {
      await createDraftThread({
        conversationId: thread.conversationId,
        input: {
          integrationId,
          customerEmail:
            kind === "email" ? (customerEmail ?? undefined) : undefined,
          customerPhone:
            kind === "sms" ? (customerPhone ?? undefined) : undefined,
          subject: kind === "email" ? replySubject : undefined,
        },
      });
    },
  );

  if (contactFormMessages.length === 0) {
    return (
      <Flex align="center" dir="column" gap="md" p="xl">
        <Text fontSize="md" textColor="secondary">
          No messages in this thread
        </Text>
      </Flex>
    );
  }

  const emailDisabledReason = !defaultEmailIntegrationId
    ? "Set a default email integration in contact form settings to reply by email."
    : !customerEmail
      ? "This submission did not include an email address."
      : null;
  // SMS reply is not yet available to merchants — surface the reason in the
  // tooltip and keep the button disabled regardless of integration/phone
  // state. Remove this when the feature ships.
  const smsDisabledReason = "Coming soon";

  return (
    <Flex dir="column" gap="none">
      <ThreadHeader
        channel="contactForm"
        isCollapsed={isCollapsed}
        latestMessageTime={latest?.sentAt}
        onClick={toggleCollapsed}
        subject={null}
      />
      {!isCollapsed && (
        <>
          {contactFormMessages.map((message) => (
            <Fragment key={message.id}>
              <ContactFormMessageCard
                message={message as typeof message & { channel: "contactForm" }}
              />
            </Fragment>
          ))}
          <Flex
            align="center"
            bgColor="tertiary"
            borderBottomWidth="1px"
            borderColor="primary"
            borderLeftWidth="1px"
            borderRightWidth="1px"
            borderStyle="solid"
            bottomLeftRadius="sm"
            bottomRightRadius="sm"
            gap="sm"
            justify="flex-start"
            p="lg"
          >
            <Tooltip arrow title={emailDisabledReason ?? ""}>
              <span>
                <RedoButton
                  disabled={!!emailDisabledReason || isCreatingDraftThread}
                  hierarchy="secondary"
                  IconLeading={MailIcon}
                  onClick={() => {
                    if (defaultEmailIntegrationId) {
                      void openReply(defaultEmailIntegrationId, "email");
                    }
                  }}
                  size="sm"
                  text="Reply with email"
                />
              </span>
            </Tooltip>
            <Tooltip arrow title={smsDisabledReason ?? ""}>
              <span>
                <RedoButton
                  disabled={!!smsDisabledReason || isCreatingDraftThread}
                  hierarchy="secondary"
                  IconLeading={MessageSquareIcon}
                  onClick={() => {
                    if (defaultSmsIntegrationId) {
                      void openReply(defaultSmsIntegrationId, "sms");
                    }
                  }}
                  size="sm"
                  text="Reply with SMS"
                />
              </span>
            </Tooltip>
          </Flex>
        </>
      )}
    </Flex>
  );
});
